const INTERNAL_ASSET_REFERENCE = /\bassets?:\/\//i

const escapeHtmlAttribute = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')

/**
 * Canonical Website Builder HTML intentionally retains asset:// references so it
 * remains portable authoring source. A browser preview must instead use the
 * resolved render copy returned by the backend.
 */
export function browserRenderableWebsiteHtml(renderHtml?: string | null): string | null {
  if (!renderHtml?.trim() || INTERNAL_ASSET_REFERENCE.test(renderHtml)) return null
  return renderHtml
}

/**
 * A blob: document has no hierarchical URL, so relative stylesheets, fonts,
 * scripts, images, and links lose the same base URL they have in the in-panel
 * srcDoc preview. Add that trusted browser base only to the ephemeral preview
 * copy. The canonical Website Builder HTML is never changed.
 */
export function standaloneWebsitePreviewHtml(
  renderHtml: string,
  previewDocumentUrl: string,
): string | null {
  const html = browserRenderableWebsiteHtml(renderHtml)
  if (!html) return null

  let baseUrl: URL
  try {
    baseUrl = new URL(previewDocumentUrl)
  } catch {
    return null
  }
  if (baseUrl.protocol !== 'http:' && baseUrl.protocol !== 'https:') return null

  // Respect an explicit author base. Otherwise reproduce the fallback base URL
  // that the same HTML receives in ChatWorkspace's srcDoc iframe.
  if (/<base\b[^>]*\bhref\s*=/i.test(html)) return html
  const base = `<base href="${escapeHtmlAttribute(baseUrl.href)}">`
  const head = /<head\b[^>]*>/i.exec(html)
  if (head?.index !== undefined) {
    const insertionPoint = head.index + head[0].length
    return `${html.slice(0, insertionPoint)}${base}${html.slice(insertionPoint)}`
  }
  const document = /<html\b[^>]*>/i.exec(html)
  if (document?.index !== undefined) {
    const insertionPoint = document.index + document[0].length
    return `${html.slice(0, insertionPoint)}<head>${base}</head>${html.slice(insertionPoint)}`
  }
  return `<!doctype html><html><head>${base}</head><body>${html}</body></html>`
}

/**
 * The generated website is untrusted. Keep it in an opaque-origin sandbox even
 * when the user asks to view it in a separate tab; never document.write it into
 * a Ros-origin/about:blank document.
 */
function previewAssetOrigin(value?: string): string | null {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null
  } catch {
    return null
  }
}

export function sandboxedPreviewWrapper(
  contentUrl: string,
  resourceBases: {assetBaseUrl?: string; previewDocumentUrl?: string} = {},
): string {
  const safeUrl = escapeHtmlAttribute(contentUrl)
  const resourceOrigins = [...new Set([
    previewAssetOrigin(resourceBases.previewDocumentUrl),
    previewAssetOrigin(resourceBases.assetBaseUrl),
  ].filter((origin): origin is string => Boolean(origin)))]
  const resourceSources = resourceOrigins.length
    ? ` ${resourceOrigins.map(escapeHtmlAttribute).join(' ')}`
    : ''
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; frame-src blob:; img-src 'self' data: blob:${resourceSources} https://www.reach3insightstop3.com https://www.rivaltech.com; media-src 'self' data: blob:${resourceSources}; font-src data:${resourceSources} https://fonts.gstatic.com; style-src 'unsafe-inline'${resourceSources} https://fonts.googleapis.com; script-src 'unsafe-inline' https://static.addtoany.com; connect-src 'none'; worker-src 'none'; object-src 'none'; base-uri 'self'${resourceSources}; form-action 'none'">
  <title>Website preview</title>
  <style>html,body,iframe{width:100%;height:100%;margin:0;border:0}body{overflow:hidden;background:#fff}</style>
</head>
<body>
  <iframe title="Website preview" src="${safeUrl}" sandbox="allow-scripts allow-forms allow-modals allow-popups allow-downloads" referrerpolicy="no-referrer"></iframe>
</body>
</html>`
}

interface WebsitePreviewWindowDependencies {
  createObjectUrl?: (blob: Blob) => string
  revokeObjectUrl?: (url: string) => void
  openWindow?: (url: string, target: string, features: string) => Window | null
  scheduleCleanup?: (callback: () => void, delayMs: number) => unknown
  previewDocumentUrl?: string
  assetBaseUrl?: string
}

export function openSandboxedWebsitePreview(
  renderHtml?: string | null,
  dependencies: WebsitePreviewWindowDependencies = {},
): boolean {
  const previewDocumentUrl = dependencies.previewDocumentUrl
    ?? (typeof document === 'undefined' ? '' : document.baseURI)
  const html = renderHtml ? standaloneWebsitePreviewHtml(renderHtml, previewDocumentUrl) : null
  if (!html) return false

  const createObjectUrl = dependencies.createObjectUrl ?? URL.createObjectURL.bind(URL)
  const revokeObjectUrl = dependencies.revokeObjectUrl ?? URL.revokeObjectURL.bind(URL)
  const openWindow = dependencies.openWindow ?? window.open.bind(window)
  const scheduleCleanup = dependencies.scheduleCleanup ?? window.setTimeout.bind(window)

  const contentUrl = createObjectUrl(new Blob([html], {type: 'text/html'}))
  const wrapperUrl = createObjectUrl(new Blob([sandboxedPreviewWrapper(contentUrl, {
    assetBaseUrl: dependencies.assetBaseUrl,
    previewDocumentUrl,
  })], {type: 'text/html'}))

  // noopener/noreferrer isolates the new tab from Ros. With noopener, browsers
  // may return null even when the tab opened, so null is not treated as failure.
  openWindow(wrapperUrl, '_blank', 'noopener,noreferrer')
  scheduleCleanup(() => {
    revokeObjectUrl(wrapperUrl)
    revokeObjectUrl(contentUrl)
  }, 60_000)
  return true
}
